/*
 * AFX QR — tiny, dependency-free QR Code generator (byte mode)
 * ──────────────────────────────────────────────────────────────────────────
 * Self-contained so the Paint Mixing Calculator can render a scannable QR for
 * a "mix recipe" deep-link entirely client-side — no third-party API, works
 * offline and in the print preview, and never ships the recipe to anyone but
 * the shopper's own phone.
 *
 * Scope: byte mode (UTF-8), versions 1–10, ECC levels L/M/Q/H. That comfortably
 * covers any /pages/paint-mixing-calculator?… deep-link (≈60–110 bytes) with
 * room to spare. Mask is auto-selected by the standard penalty score.
 *
 * Correctness is not assumed — tests/theme/afx-qr.test.cjs renders the output
 * to PNG and decodes it back with OpenCV to prove every QR actually scans.
 *
 * API:
 *   AFXQR.matrix(text, {ecc, version, mask}) -> { size, modules:boolean[][], version, ecc, mask }
 *     ecc: 'L'|'M'|'Q'|'H' (default 'M'); version/mask optional overrides (else auto).
 *   AFXQR.svg(text, {ecc, scale, quiet, dark, light}) -> SVG string
 *
 * Also runnable under Node (CommonJS export at the bottom) for tests.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;            // Node / tests
  } else {
    root.AFXQR = api;                // browser global
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ── GF(256) arithmetic, primitive polynomial 0x11D ──────────────────────
  var EXP = new Array(512), LOG = new Array(256);
  (function () {
    var x = 1;
    for (var i = 0; i < 255; i++) {
      EXP[i] = x; LOG[x] = i;
      x <<= 1;
      if (x & 0x100) x ^= 0x11D;
    }
    for (var j = 255; j < 512; j++) EXP[j] = EXP[j - 255];
  })();
  function gmul(a, b) { return (a === 0 || b === 0) ? 0 : EXP[LOG[a] + LOG[b]]; }

  // Reed–Solomon generator polynomial of the given degree (highest-degree
  // coefficient first, monic).
  function rsGenerator(degree) {
    var g = [1];
    for (var i = 0; i < degree; i++) {
      var ng = new Array(g.length + 1);
      for (var k = 0; k < ng.length; k++) ng[k] = 0;
      for (var j = 0; j < g.length; j++) {
        ng[j] ^= g[j];                       // * x  (shift up)
        ng[j + 1] ^= gmul(g[j], EXP[i]);     // * α^i
      }
      g = ng;
    }
    return g;
  }

  // EC codewords for one block of data codewords.
  function rsEncode(data, degree) {
    var g = rsGenerator(degree);
    var res = data.slice();
    for (var p = 0; p < degree; p++) res.push(0);
    for (var i = 0; i < data.length; i++) {
      var coef = res[i];
      if (coef !== 0) for (var j = 0; j < g.length; j++) res[i + j] ^= gmul(g[j], coef);
    }
    return res.slice(data.length);
  }

  // ── Error-correction block structure (ISO/IEC 18004 Table 9), v1–10 ─────
  // [ecCodewordsPerBlock, [[blockCount, dataCodewordsPerBlock], …]]
  var EC = {
    1:  { L: [7,  [[1, 19]]],            M: [10, [[1, 16]]],            Q: [13, [[1, 13]]],            H: [17, [[1, 9]]] },
    2:  { L: [10, [[1, 34]]],            M: [16, [[1, 28]]],            Q: [22, [[1, 22]]],            H: [28, [[1, 16]]] },
    3:  { L: [15, [[1, 55]]],            M: [26, [[1, 44]]],            Q: [18, [[2, 17]]],            H: [22, [[2, 13]]] },
    4:  { L: [20, [[1, 80]]],            M: [18, [[2, 32]]],            Q: [26, [[2, 24]]],            H: [16, [[4, 9]]] },
    5:  { L: [26, [[1, 108]]],           M: [24, [[2, 43]]],            Q: [18, [[2, 15], [2, 16]]],   H: [22, [[2, 11], [2, 12]]] },
    6:  { L: [18, [[2, 68]]],            M: [16, [[4, 27]]],            Q: [24, [[4, 19]]],            H: [28, [[4, 15]]] },
    7:  { L: [20, [[2, 78]]],            M: [18, [[4, 31]]],            Q: [18, [[2, 14], [4, 15]]],   H: [26, [[4, 13], [1, 14]]] },
    8:  { L: [24, [[2, 97]]],            M: [22, [[2, 38], [2, 39]]],   Q: [22, [[4, 18], [2, 19]]],   H: [26, [[4, 14], [2, 15]]] },
    9:  { L: [30, [[2, 116]]],           M: [22, [[3, 36], [2, 37]]],   Q: [20, [[4, 16], [4, 17]]],   H: [24, [[4, 12], [4, 13]]] },
    10: { L: [18, [[2, 68], [2, 69]]],   M: [26, [[4, 43], [1, 44]]],   Q: [24, [[6, 19], [2, 20]]],   H: [28, [[6, 15], [2, 16]]] }
  };

  // Alignment-pattern centre coordinates per version (v1 has none).
  var ALIGN = {
    1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
    6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50]
  };

  function dataCodewords(version, ecc) {
    var groups = EC[version][ecc][1], n = 0;
    for (var i = 0; i < groups.length; i++) n += groups[i][0] * groups[i][1];
    return n;
  }

  // UTF-8 encode a string to a byte array.
  function utf8(str) {
    var s = unescape(encodeURIComponent(str)), out = [];
    for (var i = 0; i < s.length; i++) out.push(s.charCodeAt(i) & 0xff);
    return out;
  }

  function pickVersion(byteLen, ecc) {
    for (var v = 1; v <= 10; v++) {
      var ccBits = v <= 9 ? 8 : 16;          // byte-mode char-count length
      var need = 4 + ccBits + byteLen * 8;
      if (need <= dataCodewords(v, ecc) * 8) return v;
    }
    return null;                              // too long for v1–10
  }

  // Build the final (interleaved data + EC) codeword stream.
  function makeCodewords(bytes, version, ecc) {
    var bits = [];
    function push(val, len) { for (var i = len - 1; i >= 0; i--) bits.push((val >> i) & 1); }
    push(0x4, 4);                             // byte mode
    push(bytes.length, version <= 9 ? 8 : 16);
    for (var i = 0; i < bytes.length; i++) push(bytes[i], 8);

    var capBits = dataCodewords(version, ecc) * 8;
    for (var t = 0, term = Math.min(4, capBits - bits.length); t < term; t++) bits.push(0);
    while (bits.length % 8 !== 0) bits.push(0);
    var pads = [0xEC, 0x11], pi = 0;
    while (bits.length < capBits) { push(pads[pi % 2], 8); pi++; }

    var data = [];
    for (var b = 0; b < bits.length; b += 8) {
      var byte = 0;
      for (var k = 0; k < 8; k++) byte = (byte << 1) | bits[b + k];
      data.push(byte);
    }

    var spec = EC[version][ecc], ecLen = spec[0], groups = spec[1];
    var blocks = [], idx = 0;
    for (var gi = 0; gi < groups.length; gi++) {
      for (var c = 0; c < groups[gi][0]; c++) {
        var d = data.slice(idx, idx + groups[gi][1]); idx += groups[gi][1];
        blocks.push({ d: d, e: rsEncode(d, ecLen) });
      }
    }
    var out = [], maxD = 0;
    for (var bi = 0; bi < blocks.length; bi++) maxD = Math.max(maxD, blocks[bi].d.length);
    for (var di = 0; di < maxD; di++) for (var bj = 0; bj < blocks.length; bj++)
      if (di < blocks[bj].d.length) out.push(blocks[bj].d[di]);
    for (var ei = 0; ei < ecLen; ei++) for (var bk = 0; bk < blocks.length; bk++)
      out.push(blocks[bk].e[ei]);
    return out;
  }

  // ── Matrix assembly ─────────────────────────────────────────────────────
  function buildMatrix(codewords, version) {
    var size = 17 + version * 4;
    var m = [], reserved = [];
    for (var r = 0; r < size; r++) {
      m.push(new Array(size).fill(0));
      reserved.push(new Array(size).fill(false));
    }
    function set(rr, cc, val) { m[rr][cc] = val ? 1 : 0; reserved[rr][cc] = true; }

    // Finder pattern + separator at a corner.
    function finder(tr, tc) {
      for (var dr = -1; dr <= 7; dr++) for (var dc = -1; dc <= 7; dc++) {
        var rr = tr + dr, cc = tc + dc;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        var on = (dr >= 0 && dr <= 6 && (dc === 0 || dc === 6)) ||
                 (dc >= 0 && dc <= 6 && (dr === 0 || dr === 6)) ||
                 (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4);
        set(rr, cc, on);
      }
    }
    finder(0, 0); finder(0, size - 7); finder(size - 7, 0);

    // Timing patterns.
    for (var i = 8; i < size - 8; i++) {
      if (!reserved[6][i]) set(6, i, i % 2 === 0);
      if (!reserved[i][6]) set(i, 6, i % 2 === 0);
    }

    // Alignment patterns — every (row,col) from the position list except the
    // three that overlap the finder patterns. Note: patterns on the timing
    // line (e.g. centre row/col 6) ARE drawn and take precedence over timing.
    var pos = ALIGN[version], last = pos.length - 1;
    for (var a = 0; a < pos.length; a++) for (var b = 0; b < pos.length; b++) {
      if ((a === 0 && b === 0) || (a === 0 && b === last) || (a === last && b === 0)) continue;
      var cr = pos[a], cc2 = pos[b];
      for (var yr = -2; yr <= 2; yr++) for (var xc = -2; xc <= 2; xc++) {
        var on2 = Math.max(Math.abs(yr), Math.abs(xc)) !== 1;
        set(cr + yr, cc2 + xc, on2);
      }
    }

    // Dark module + reserve format/version info areas.
    set(size - 8, 8, 1);
    for (var k = 0; k <= 8; k++) {
      if (!reserved[8][k]) reserved[8][k] = true;
      if (!reserved[k][8]) reserved[k][8] = true;
    }
    for (var k2 = 0; k2 < 8; k2++) {
      reserved[8][size - 1 - k2] = true;
      reserved[size - 1 - k2][8] = true;
    }
    if (version >= 7) {
      for (var vr = 0; vr < 6; vr++) for (var vc = 0; vc < 3; vc++) {
        reserved[vr][size - 11 + vc] = true;
        reserved[size - 11 + vc][vr] = true;
      }
    }

    // Place data bits in the standard upward/downward zig-zag.
    var bitArr = [];
    for (var ci = 0; ci < codewords.length; ci++)
      for (var bb = 7; bb >= 0; bb--) bitArr.push((codewords[ci] >> bb) & 1);
    var bp = 0, up = true;
    for (var col = size - 1; col > 0; col -= 2) {
      if (col === 6) col = 5;                   // skip vertical timing column
      for (var row = 0; row < size; row++) {
        var rr2 = up ? size - 1 - row : row;
        for (var s = 0; s < 2; s++) {
          var cc3 = col - s;
          if (!reserved[rr2][cc3]) m[rr2][cc3] = bp < bitArr.length ? bitArr[bp++] : 0;
        }
      }
      up = !up;
    }
    return { m: m, reserved: reserved, size: size };
  }

  // ── Masking + format/version info ───────────────────────────────────────
  var MASKS = [
    function (i, j) { return (i + j) % 2 === 0; },
    function (i)    { return i % 2 === 0; },
    function (i, j) { return j % 3 === 0; },
    function (i, j) { return (i + j) % 3 === 0; },
    function (i, j) { return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0; },
    function (i, j) { return ((i * j) % 2) + ((i * j) % 3) === 0; },
    function (i, j) { return (((i * j) % 2) + ((i * j) % 3)) % 2 === 0; },
    function (i, j) { return (((i + j) % 2) + ((i * j) % 3)) % 2 === 0; }
  ];

  var ECC_BITS = { L: 1, M: 0, Q: 3, H: 2 };

  function bch(value, gen, genBits) {
    var v = value;
    while (Math.floor(Math.log(v) / Math.log(2)) >= genBits)
      v ^= gen << (Math.floor(Math.log(v) / Math.log(2)) - genBits);
    return v;
  }

  function formatBits(ecc, mask) {
    var data = (ECC_BITS[ecc] << 3) | mask;
    var rem = bch(data << 10, 0x537, 10);
    return ((data << 10) | rem) ^ 0x5412;     // 15 bits
  }

  function versionBits(version) {
    var rem = bch(version << 12, 0x1F25, 12);
    return (version << 12) | rem;              // 18 bits
  }

  function applyMaskAndInfo(grid, version, ecc, mask) {
    var size = grid.size, m = [];
    for (var r = 0; r < size; r++) m.push(grid.m[r].slice());
    for (var i = 0; i < size; i++) for (var j = 0; j < size; j++)
      if (!grid.reserved[i][j] && MASKS[mask](i, j)) m[i][j] ^= 1;

    var fmt = formatBits(ecc, mask);
    for (var k = 0; k < 15; k++) {
      var bit = (fmt >> (14 - k)) & 1;       // format string is placed MSB-first
      // Around top-left finder.
      if (k < 6) m[8][k] = bit;
      else if (k === 6) m[8][7] = bit;
      else if (k === 7) m[8][8] = bit;
      else if (k === 8) m[7][8] = bit;
      else m[14 - k][8] = bit;
      // Mirror near top-right / bottom-left finders.
      if (k < 8) m[size - 1 - k][8] = bit;
      else m[8][size - 15 + k] = bit;
    }
    m[size - 8][8] = 1;                        // dark module (always set, after format)

    if (version >= 7) {
      var vb = versionBits(version);
      for (var b = 0; b < 18; b++) {
        var vbit = (vb >> b) & 1, rr = Math.floor(b / 3), cc = b % 3;
        m[rr][size - 11 + cc] = vbit;
        m[size - 11 + cc][rr] = vbit;
      }
    }
    return { m: m, size: size };
  }

  function penalty(m, size) {
    var score = 0, i, j, k;
    // Rule 1: runs of ≥5 same-colour modules in rows and columns.
    for (i = 0; i < size; i++) for (var dir = 0; dir < 2; dir++) {
      var run = 1, prev = -1;
      for (j = 0; j < size; j++) {
        var v = dir === 0 ? m[i][j] : m[j][i];
        if (v === prev) { run++; if (run === 5) score += 3; else if (run > 5) score += 1; }
        else { run = 1; prev = v; }
      }
    }
    // Rule 2: 2×2 blocks of the same colour.
    for (i = 0; i < size - 1; i++) for (j = 0; j < size - 1; j++) {
      var c = m[i][j];
      if (c === m[i][j + 1] && c === m[i + 1][j] && c === m[i + 1][j + 1]) score += 3;
    }
    // Rule 3: finder-like 1:1:3:1:1 patterns (with 4 light modules either side).
    var pat1 = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0], pat2 = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
    function match(getter, a) {
      for (k = 0; k <= size - 11; k++) {
        var ok1 = true, ok2 = true;
        for (var t = 0; t < 11; t++) {
          var val = getter(a, k + t);
          if (val !== pat1[t]) ok1 = false;
          if (val !== pat2[t]) ok2 = false;
        }
        if (ok1 || ok2) score += 40;
      }
    }
    for (i = 0; i < size; i++) {
      match(function (row, c) { return m[row][c]; }, i);
      match(function (col, r) { return m[r][col]; }, i);
    }
    // Rule 4: deviation of dark-module proportion from 50%.
    var dark = 0;
    for (i = 0; i < size; i++) for (j = 0; j < size; j++) dark += m[i][j];
    var pct = (dark * 100) / (size * size);
    score += Math.floor(Math.abs(pct - 50) / 5) * 10;
    return score;
  }

  // ── Public API ──────────────────────────────────────────────────────────
  function matrix(text, opts) {
    opts = opts || {};
    var ecc = (opts.ecc || 'M').toUpperCase();
    if (!ECC_BITS.hasOwnProperty(ecc)) ecc = 'M';
    var bytes = utf8(String(text == null ? '' : text));
    var version = (opts.version && opts.version >= 1 && opts.version <= 10) ? opts.version : pickVersion(bytes.length, ecc);
    // A caller-forced version that can't hold the payload would emit a corrupt,
    // unscannable code — auto-bump to the smallest version that fits instead.
    if (version) {
      var ccBits = version <= 9 ? 8 : 16;
      if (4 + ccBits + bytes.length * 8 > dataCodewords(version, ecc) * 8) version = pickVersion(bytes.length, ecc);
    }
    if (!version) throw new Error('AFXQR: payload too long for v1–10 at ECC ' + ecc);

    var grid = buildMatrix(makeCodewords(bytes, version, ecc), version);
    var best = null, bestScore = Infinity, bestMask = 0;
    var lo = 0, hi = 7;
    if (opts.mask != null && opts.mask >= 0 && opts.mask <= 7) { lo = hi = opts.mask; }
    for (var mask = lo; mask <= hi; mask++) {
      var masked = applyMaskAndInfo(grid, version, ecc, mask);
      var p = penalty(masked.m, masked.size);
      if (p < bestScore) { bestScore = p; best = masked; bestMask = mask; }
    }
    var bool = [];
    for (var r = 0; r < best.size; r++) {
      var row = [];
      for (var c = 0; c < best.size; c++) row.push(best.m[r][c] === 1);
      bool.push(row);
    }
    return { size: best.size, modules: bool, version: version, ecc: ecc, mask: bestMask };
  }

  function svg(text, opts) {
    opts = opts || {};
    var scale = opts.scale || 4, quiet = opts.quiet == null ? 4 : opts.quiet;
    var dark = opts.dark || '#16181C', light = opts.light || '#FFFFFF';
    var q = matrix(text, opts), n = q.size, dim = (n + quiet * 2) * scale;
    var rects = [];
    for (var r = 0; r < n; r++) for (var c = 0; c < n; c++) if (q.modules[r][c])
      rects.push('M' + ((c + quiet) * scale) + ' ' + ((r + quiet) * scale) +
                 'h' + scale + 'v' + scale + 'h-' + scale + 'z');
    return '<svg xmlns="http://www.w3.org/2000/svg" width="' + dim + '" height="' + dim +
      '" viewBox="0 0 ' + dim + ' ' + dim + '" shape-rendering="crispEdges" role="img">' +
      '<rect width="' + dim + '" height="' + dim + '" fill="' + light + '"/>' +
      '<path fill="' + dark + '" d="' + rects.join('') + '"/></svg>';
  }

  return { matrix: matrix, svg: svg, _internal: { rsEncode: rsEncode, formatBits: formatBits,
    versionBits: versionBits, dataCodewords: dataCodewords, pickVersion: pickVersion } };
});
